import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../lib/api';
import type { FamilySimpleInfo } from '../../lib/api';
import { getPointsIcon } from '@/lib/utils/pointsIcon';

const POINT_NAMES = ['星星', '金币', '友爱分', '彩虹豆', '勇气值', '钻石', '阳光', '月亮', '花朵', '皇冠', '火箭', '音符', '书本', '奖杯', '奖章'];

export default function WelcomePage() {
  const { families, currentFamily, refreshFamilies, setCurrentFamily } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [familyName, setFamilyName] = useState('');
  const [pointName, setPointName] = useState('星星');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 已有家庭 → 直接跳 dashboard
  if (families.length > 0 || currentFamily) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  const handleCreate = async () => {
    if (!familyName.trim()) {
      setError('请输入家庭名称');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.family.createFamily({
        name: familyName.trim(),
        point_name: pointName,
      });
      if (res.code === 0 && res.data) {
        await refreshFamilies();
        const updated = await api.family.getMyFamilies();
        if (updated.code === 0 && updated.data && updated.data.length > 0) {
          setCurrentFamily(updated.data[0] as unknown as FamilySimpleInfo);
        }
        // 跳转到成员管理页，引导添加儿童
        navigate('/members', { replace: true });
      } else {
        setError(res.message || '创建失败');
      }
    } catch (e: any) {
      setError(e?.message || '创建失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #E8EAF6 0%, #F3E5F5 50%, #E0F7FA 100%)' }}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl">
        {/* 头部 */}
        <div className="p-6 text-center" style={{ background: 'linear-gradient(135deg, #1565C0, #1E88E5)' }}>
          <div className="text-5xl mb-3">🌍</div>
          <h1 className="text-white text-xl font-bold">欢迎加入习惯星球</h1>
          <p className="text-white/70 text-sm mt-2">创建您的家庭，开始培养好习惯</p>
          {/* 步骤指示 */}
          <div className="flex items-center justify-center gap-4 mt-4">
            <div className={`flex items-center gap-1.5 text-xs ${step === 1 ? 'text-white font-bold' : 'text-white/50'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step === 1 ? 'bg-white text-blue-600' : 'bg-white/20'}`}>1</span>
              创建家庭
            </div>
            <div className="w-8 h-px bg-white/30"></div>
            <div className={`flex items-center gap-1.5 text-xs ${step === 2 ? 'text-white font-bold' : 'text-white/50'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step === 2 ? 'bg-white text-blue-600' : 'bg-white/20'}`}>2</span>
              添加儿童
            </div>
          </div>
        </div>

        {/* 表单 */}
        <div className="p-6 space-y-5">
          {/* 家庭名称 */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
              <i className="ri-home-heart-fill text-rose-400"></i>
              家庭名称
            </label>
            <input
              value={familyName}
              onChange={e => { setFamilyName(e.target.value); setError(''); }}
              placeholder="例如：张家、我的家庭"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-400 transition-colors"
              autoFocus
            />
          </div>

          {/* 积分名称 */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
              <i className={`${getPointsIcon(pointName)} text-amber-400`}></i>
              积分名称
            </label>
            <div className="grid grid-cols-5 gap-2 mb-3">
              {POINT_NAMES.map(n => (
                <button
                  key={n}
                  onClick={() => setPointName(n)}
                  className={`flex items-center justify-center gap-1 px-2 py-2 rounded-xl text-xs font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                    pointName === n ? 'border-amber-400 text-amber-600 bg-amber-50' : 'border-gray-200 text-gray-500 hover:border-amber-200 hover:bg-amber-50/50'
                  }`}
                >
                  <i className={`${getPointsIcon(n)} text-sm`}></i>
                  {n}
                </button>
              ))}
            </div>
            <input
              value={pointName}
              onChange={e => setPointName(e.target.value)}
              placeholder="或自定义积分名称"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 transition-colors"
            />
            <p className="text-xs text-gray-400 mt-1.5">
              <i className={`${getPointsIcon(pointName)} text-amber-400`}></i>
              当前积分名称：{pointName || '星星'}
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}

          <button
            onClick={handleCreate}
            disabled={loading || !familyName.trim()}
            className="w-full py-3 rounded-xl text-white text-sm font-medium transition-all cursor-pointer disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #1565C0, #1E88E5)' }}
          >
            {loading ? '创建中...' : '创建家庭并添加儿童'}
          </button>

          <p className="text-xs text-gray-400 text-center">
            创建后将引导您添加第一个儿童
          </p>
        </div>
      </div>
    </div>
  );
}
